<template>
  <f7-page name="informe-propuesta">
    <f7-navbar :title="$t('informeProp.titulo')" :back-link="$t('atras')"></f7-navbar>
    <!-- #16 · Pantalla mixta: el informe es el registro auditado de una corrida, congelado desde
         que se lanzo (y del historico se abren corridas de hace meses), asi que lleva SU fecha y
         no "en vivo"; el listado del historico si se consulta en cada entrada. -->
    <div class="sello-datos-bar">
      <sello-datos v-if="reg" estado="fecha" :fecha="reg.solicitud.fecha"
        :nota="$t('informeProp.selloCorrida')" />
      <sello-datos estado="vivo" :nota="$t('informeProp.selloHistorico')" />
    </div>

    <!-- SPEC-INFORME-PROPUESTA: presentación del registro auditado (motor y registro intactos).
         Mockup: claude\informe-propuesta-planificacion.html — estilo del dashboard de Control. -->
    <div class="ipp">
      <div class="wrap">
        <div v-if="cargando" class="foot">{{ $t('informeProp.cargando') }}</div>

        <template v-if="reg">
          <!-- HERO -->
          <div class="hero">
            <div class="hero-top">
              <div>
                <h1>{{ $t('informeProp.titulo') }}</h1>
                <div class="sub">
                  <span>👤 {{ $t('informeProp.inspector') }} <b>{{ reg.solicitud.inspector }}</b></span>
                  <span>📅 {{ $t('informeProp.ventana') }} <b>{{ fmtFecha(reg.solicitud.desde) }} – {{ fmtFecha(reg.solicitud.hasta) }}</b></span>
                  <span class="estado" :class="reg.resolucion.estado === 'PROPUESTA' ? 'e-prop' : 'e-acep'">
                    <span class="b"></span>{{ etiquetaEstado(reg.resolucion.estado) }}</span>
                </div>
                <div class="who">{{ $t('informeProp.solicitadaPor') }} <b>{{ reg.solicitud.usuarioSolicitante }}</b> ·
                  {{ fmtFechaHora(reg.solicitud.fecha) }}
                  <template v-if="fechaResolucion"> &nbsp;·&nbsp; {{ $t('informeProp.resuelta') }} {{ fmtFechaHora(fechaResolucion) }}</template>
                </div>
              </div>
              <div class="hero-right">
                <span class="enac"><span class="dot"></span>{{ $t('informeProp.determinista') }}</span>
                <div class="runbox">
                  run <b>{{ reg.resultado.runId || '—' }}</b><br>
                  hash <b>{{ reg.resultado.entradaHash || '—' }}</b>
                </div>
              </div>
            </div>
          </div>

          <!-- KPIs -->
          <div class="kpis">
            <!-- #460 · «Visitas propuestas» se cuenta en la MISMA unidad que «Aceptadas» de la
                 derecha y que la cronología de abajo: el trabajo de un día. Antes enseñaba los
                 bloques del solver (`totales.nPropuestas`), así que una visita partida en tramos
                 hacía que los dos KPI de la misma corrida dieran cifras distintas. La unidad se
                 elige en un solo sitio, `RecuentoPropuesta`. -->
            <div class="kpi accent"><div class="lab">{{ $t('informeProp.kpiVisitas') }}</div>
              <div class="val">{{ trabajosPropuestos }}</div>
              <div class="ctx" :title="$t('informeProp.kpiVisitasAyuda')">{{ textoEnDias(dias.length) }}</div></div>
            <div class="kpi"><div class="lab">{{ $t('informeProp.kpiImporte') }}</div>
              <div class="val small">{{ eur(importePropuesto) }}</div>
              <div class="ctx">{{ $t('informeProp.repartoVisitas') }}</div></div>
            <div class="kpi"><div class="lab">{{ $t('informeProp.kpiNoColocadas') }}</div>
              <div class="val">{{ totales.nNoPlanificables || 0 }}</div>
              <div class="ctx">{{ eur(importeNoColocadas) }} · {{ textoPorMotivos(motivosNoColocadas) }}</div></div>
            <div class="kpi"><div class="lab">{{ $t('informeProp.kpiAntiguas') }}</div>
              <div class="val">{{ totales.nLineasAntiguas || 0 }}</div>
              <div class="ctx">{{ $t('informeProp.aRevisar') }}</div></div>
            <!-- Capa 3: km del registro (retrocompat: registros antiguos sin kmTotal → "—").
                 #460 · Son los km que ESTIMÓ la propuesta (suma de `desplazamientoPropuesto`, en
                 línea recta × 1,3 sobre centroides de CP), NO los de la ruta de Google: el rótulo lo
                 dice, porque leerlos como "la ruta" hizo dudar de la medida entera (prueba del
                 15/10/2026). Los de Google viven en la comparación de la #460, que no tiene pantalla. -->
            <div class="kpi"><div class="lab">{{ $t('informeProp.kpiRuta') }}</div>
              <div class="val small">{{ totales.kmTotal != null ? n1(totales.kmTotal) + ' km' : '—' }}</div>
              <div class="ctx" :title="$t('informeProp.kpiRutaAyuda')">{{ horasCampo }} {{ $t('informeProp.campoCorto') }}<template v-if="totales.tiempoDesplazamientoMin != null"> · {{ horas(totales.tiempoDesplazamientoMin) }} {{ $t('informeProp.tdCorto') }}</template></div></div>
            <!-- #460 (opción A) · Aceptadas se cuenta por UNIDAD ACEPTABLE —lo que el Aceptar crea
                 como un alta—, leyendo el plan que la API derivó de la propuesta de la corrida y las
                 altas que acreditó. No se deduce aquí: la regla depende del tipo de trabajo y tenerla
                 copiada en la pantalla es lo que descuadró el cociente cuatro rondas seguidas
                 (26 − 2 fallos ≠ 23 de la prueba del 15/10/2026). Si la corrida no trae plan, se dice
                 con qué se cuenta. -->
            <div class="kpi accent"><div class="lab">{{ $t('informeProp.kpiAceptadas') }}</div>
              <div class="val">{{ trabajosAceptados }}<span class="de-total"> / {{ trabajosPropuestos }}</span></div>
              <div class="ctx" :title="$t('informeProp.kpiAceptadasAyuda')" v-if="aceptadas.length">{{ $t('informeProp.porUsuario', { u: aceptadas[0].usuario }) }}</div>
              <div class="ctx ctx-nota" v-if="notaRecuento">{{ notaRecuento }}</div></div>
          </div>

          <!-- S1 · PARÁMETROS -->
          <div class="section">
            <div class="sec-head"><span class="bar"></span><h2>{{ $t('informeProp.s1') }}</h2>
              <span class="hint">{{ $t('informeProp.s1hint') }}</span></div>
            <div class="sec-body">
              <div class="grid">
                <div class="field"><div class="k">{{ $t('informeProp.horizonte') }}</div>
                  <div class="v">{{ diasVentana }} {{ palabraDias(diasVentana) }} <small>({{ fmtFecha(reg.solicitud.desde) }} – {{ fmtFecha(reg.solicitud.hasta) }})</small></div></div>
                <div class="field"><div class="k">{{ $t('informeProp.alcance') }}</div>
                  <div class="v">{{ par.alcance || 'HORIZONTE' }}</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.modo') }}</div>
                  <div class="v">{{ par.modo || 'CUBRIR_HUECOS' }}</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.comida') }}</div>
                  <div class="v">{{ par.comidaInicioMin || '13:00' }}–{{ par.comidaInicioMax || '15:30' }}
                    <small>· pref {{ par.comidaPreferente || '14:00' }} · {{ par.comidaDuracionMin || 45 }} min</small></div></div>
                <div class="field"><div class="k">{{ $t('informeProp.inicioJornada') }}</div>
                  <div class="v">{{ par.jornadaInicioPreferente || '07:45' }} <small>· mín {{ par.jornadaInicioMin || '06:00' }}</small></div></div>
                <div class="field"><div class="k">{{ $t('informeProp.pernocta') }}</div>
                  <div class="v">{{ triestado(par.planificarConPernocta) }}</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.proponerInformes') }}</div>
                  <div class="v">{{ triestado(par.proponerInformes) }}</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.sueloAntiguedad') }}</div>
                  <div class="v">{{ par.mesesAntiguedadMax || 24 }} {{ palabraMeses(par.mesesAntiguedadMax || 24) }}</div></div>
              </div>
            </div>
          </div>

          <!-- S2 · SNAPSHOT RESTRICCIONES -->
          <div class="section">
            <div class="sec-head"><span class="bar bar-azul"></span><h2>{{ $t('informeProp.s2') }}</h2>
              <span class="hint"><span class="frozen">{{ $t('informeProp.snapshot') }} · {{ fmtFecha(reg.solicitud.fecha) }}</span></span></div>
            <div class="sec-body">
              <p class="nota">{{ $t('informeProp.s2nota') }}</p>
              <div class="grid" v-if="snap">
                <div class="field"><div class="k">{{ $t('informeProp.base') }}</div>
                  <div class="v">{{ snap.baseDireccion || '—' }} <small v-if="snap.baseLat">({{ snap.baseLat.toFixed(3) }}, {{ snap.baseLon.toFixed(3) }})</small></div></div>
                <div class="field"><div class="k">{{ $t('informeProp.radioMax') }}</div>
                  <div class="v">{{ snap.radioMaxKm ? snap.radioMaxKm + ' km' : $t('informeProp.noLimite') }}</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.jornadaTope') }}</div>
                  <div class="v">{{ snap.topeHorasDiaOverride || 10 }} h/{{ $t('informeProp.dias').slice(0, 3) }} · 46 h/sem</div></div>
                <div class="field"><div class="k">{{ $t('informeProp.pernocta') }}</div>
                  <div class="v">{{ snap.pernoctaPermitida ? $t('informeProp.si') : $t('informeProp.no') }}
                    <small v-if="snap.maxNochesSeguidas">· máx {{ snap.maxNochesSeguidas }}</small></div></div>
                <div class="field"><div class="k">{{ $t('informeProp.prefInformes') }}</div>
                  <div class="v">{{ snap.diaPreferidoInformes || '—' }}</div></div>
                <div class="field" v-if="snap.notas"><div class="k">{{ $t('informeProp.notas') }}</div>
                  <div class="v"><small>{{ snap.notas }}</small></div></div>
                <div class="field span2"><div class="k">{{ $t('informeProp.provinciasCubre') }}</div>
                  <div class="chips"><span class="chip" v-for="p in (snap.zonasCubre || [])" :key="'zc-' + p">{{ p }}</span>
                    <span v-if="!(snap.zonasCubre || []).length">—</span></div></div>
                <div class="field span2"><div class="k">{{ $t('informeProp.provinciasVetadas') }}</div>
                  <div class="chips"><span class="chip veto" v-for="p in (snap.zonasVetadas || [])" :key="'zv-' + p">{{ p }}</span>
                    <span v-if="!(snap.zonasVetadas || []).length">—</span></div></div>
              </div>
            </div>
          </div>

          <!-- S3 · PROCESO DEDUCTIVO -->
          <div class="section">
            <div class="sec-head"><span class="bar bar-verde"></span><h2>{{ $t('informeProp.s3') }}</h2>
              <span class="hint">{{ $t('informeProp.s3hint') }} · {{ $t('informeProp.criterioCorrida') }} <b>{{ criterioCorrida }}</b></span></div>
            <div class="sec-body">
              <div class="leyenda">
                <span v-for="(m, k) in MOTIVOS" :key="'lg-' + k" class="badge" :class="m.clase">{{ m.badge }}</span>
              </div>
              <div class="day" v-for="(dia, di) in dias" :key="'d-' + dia.clave" :class="{ collapsed: di > 0 && !abiertos[dia.clave] }">
                <div class="day-head" @click="$set(abiertos, dia.clave, !abiertos[dia.clave])">
                  <span class="dchip">{{ dia.etiqueta }}</span>
                  <!-- #460 · visitas (trabajos) del día, no filas: el solver parte una visita larga
                       en varios tramos y cada tramo es una fila de abajo, que lo dice. Así la suma
                       de las cabeceras de día cuadra con el KPI «Visitas propuestas». -->
                  <span class="dtitle">{{ dia.nTrabajos }} {{ palabraVisitas(dia.nTrabajos) }}</span>
                  <span class="dmeta">
                    <!-- #460 · «colocado» son los minutos que PROPUSO la corrida, que es lo que
                         mide esta pantalla. El tooltip lo dice, y si alguien editó una duración al
                         aceptar el día lo avisa en la nota de al lado en vez de dejar que el
                         informe y el calendario se contradigan en silencio. -->
                    <span :title="$t('informeProp.colocadoAyuda')">{{ $t('informeProp.colocado') }} <b>{{ horas(dia.minutos) }}</b>
                      <span class="barmini"><i :style="{ width: Math.min(100, Math.round(dia.minutos / 480 * 100)) + '%' }"></i></span></span>
                    <span class="dnota" v-if="dia.editadas" :title="$t('informeProp.duracionEditadaAyuda')">{{ textoDuracionEditada(dia.editadas) }}</span>
                    <!-- Capa 3: km del día (registro.resultado.kmPorDia; antiguo sin dato → oculto).
                         #460: estimados por la propuesta, como el KPI de arriba. -->
                    <span v-if="kmDelDia(dia.clave) != null" :title="$t('informeProp.kpiRutaAyuda')"><b>{{ n1(kmDelDia(dia.clave)) }} km</b> {{ $t('informeProp.estCorto') }}</span>
                    <span class="caret">▼</span>
                  </span>
                </div>
                <div class="day-body">
                  <!-- #460 · el bloque que el plan NO convierte en alta se ve apagado y su fila dice
                       por qué: así la cabecera del día («N visitas», las altas que salen de ahí) no
                       parece contradecir lo que hay debajo. -->
                  <div class="visit" v-for="v in dia.visitas" :key="'v-' + v.clave" :class="{ 'visit-omitida': v.omitido }">
                    <div class="time">{{ v.hora }}<small>{{ v.minutos }} min</small></div>
                    <div>
                      <div class="place">{{ v.lugar }}</div>
                      <div class="svc">{{ v.servicio }}</div>
                      <div class="reason" v-if="v.explicacion">
                        <span class="badge" :class="claseMotivo(v.explicacion.motivoColocacion)">{{ badgeMotivo(v.explicacion.motivoColocacion) }}</span>
                        <span class="detalle">{{ v.explicacion.detalle }}</span>
                        <span class="crit-chip" v-if="v.explicacion.criterio">{{ v.explicacion.criterio }}</span>
                      </div>
                      <!-- Pulido 1: TPI etiquetado como tal; solo el CAMPO sin motivo queda como anomalía -->
                      <div class="reason" v-else-if="v.esTpi"><span class="tpi-chip">{{ $t('informeProp.tpip') }}</span></div>
                      <div class="reason" v-else><span class="detalle detalle-mut">{{ $t('informeProp.sinMotivo') }}</span></div>
                      <div class="alt" v-if="v.explicacion && v.explicacion.alternativaDescartada">{{ v.explicacion.alternativaDescartada }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- POR QUÉ NO -->
          <div class="section" v-if="(reg.resultado.noPlanificables || []).length">
            <div class="sec-head"><span class="bar bar-roja"></span>
              <h2>{{ textoPorqueNo(reg.resultado.noPlanificables.length) }}</h2>
              <span class="hint">{{ $t('informeProp.fueraPropuesta', { eur: eur(importeNoColocadas) }) }}</span></div>
            <!-- Pulido 2: layout FIJO — Importe e "Ir al pedido" siempre visibles sin scroll; Cliente y
                 Población truncan con ellipsis y el badge de motivo hace wrap. -->
            <div class="sec-body sec-tabla">
              <table class="tabla-no">
                <colgroup><col class="c-ref"><col class="c-cli"><col class="c-pob"><col class="c-mot">
                  <col class="c-imp"><col class="c-link"></colgroup>
                <thead><tr><th>{{ $t('informeProp.pedidoLinea') }}</th><th>{{ $t('informeProp.cliente') }}</th>
                  <th>{{ $t('informeProp.poblacion') }}</th><th>{{ $t('informeProp.motivo') }}</th>
                  <th class="der">{{ $t('informeProp.importe') }}</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="(np, i) in noColocadas" :key="'np-' + i">
                    <td class="trunca">{{ np.referencia }}</td>
                    <td class="trunca" :title="np.cliente">{{ np.cliente || '—' }}</td>
                    <td class="trunca" :title="np.poblacion">{{ np.poblacion || '—' }}</td>
                    <td><span class="np-badge np-wrap">{{ np.detalle || np.motivo || '—' }}</span></td>
                    <td class="imp">{{ np.importe != null ? eur(np.importe) : '—' }}</td>
                    <td class="c-accion"><a class="link" v-if="np.idPedido" :href="'/pedidos/view/' + np.idPedido + '/'">{{ $t('informeProp.irAlPedido') }}</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- S4 · RESOLUCIÓN -->
          <div class="section">
            <div class="sec-head"><span class="bar bar-ok"></span><h2>{{ $t('informeProp.s4') }}</h2>
              <span class="hint">{{ $t('informeProp.s4hint') }}<template v-if="aceptadas.length"> · {{ $t('informeProp.s4hintTrabajos') }}</template></span></div>
            <div class="sec-body">
              <div class="tl">
                <div class="ev">
                  <div class="et">{{ $t('informeProp.propuestaGenerada') }}</div>
                  <!-- #460 · misma unidad que la fila de abajo («N de M»): las dos entradas de la
                       cronología hablan de la misma corrida y no pueden traer denominadores
                       distintos. -->
                  <div class="em">{{ reg.solicitud.usuarioSolicitante }} · {{ fmtFechaHora(reg.solicitud.fecha) }} ·
                    {{ textoVisitasPropuestas(trabajosPropuestos) }}</div>
                </div>
                <div class="ev done" v-if="aceptadas.length">
                  <div class="et">{{ etiquetaEstado(reg.resolucion.estado === 'ACEPTADA' ? 'ACEPTADA' : 'ACEPTADA_PARCIAL') }}</div>
                  <div class="em">{{ aceptadas[0].usuario }} · {{ fmtFechaHora(aceptadas[0].fecha) }} ·
                    {{ textoAceptadasDe(trabajosAceptados, trabajosPropuestos) }}</div>
                  <!-- #460 (opción A) · Una píldora por UNIDAD ACREDITADA —unidad del plan con alta
                       de verdad creada—, y cada una DICE QUÉ ES porque el tipo viene en el plan: un
                       informe de oficina ya no se deduce por no traer pedido, que es lo que dejaba
                       tres píldoras mudas «✓ · / L» (prueba del 15/10/2026). Y lo que falta se
                       enseña en vez de quedar como la diferencia de un cociente: una aceptación
                       parcial conserva sus unidades pendientes y aquí se ven.
                       Las corridas sin plan siguen pintando sus aceptadas trazadas, que es lo que
                       tienen: no se les inventa una correspondencia que nunca se guardó. -->
                  <div class="accepted" v-if="hayPlan">
                    <span class="acc-pill" v-for="(u, i) in unidadesAcreditadas" :key="'au-' + i">
                      ✓ {{ etiquetaUnidad(u) }}</span>
                    <!-- La píldora gris DICE SU MOTIVO: está guardado en el descarte y dejarlo en
                         el tooltip obligaba a ir al registro para saber qué pasó con ese trabajo
                         (prueba de dirección del 16/10/2026). El tooltip pasa a decir quién y
                         cuándo, que es lo que no cabe en la píldora. -->
                    <span class="acc-pill acc-desc" v-for="(d, i) in unidadesDescartadas" :key="'du-' + i"
                          :title="ayudaDescartada(d.descarte)">
                      ✕ {{ etiquetaUnidad(d.unidad) }}<em class="pill-motivo"
                        v-if="d.descarte.motivo"> · {{ d.descarte.motivo }}</em></span>
                    <span class="acc-pill acc-pend" v-for="(u, i) in unidadesPendientes" :key="'pu-' + i"
                          :title="$t('informeProp.pendienteAyuda')">
                      ◻ {{ etiquetaUnidad(u) }}</span>
                  </div>
                  <div class="accepted" v-else>
                    <span class="acc-pill" v-for="(a, i) in aceptadas" :key="'ac-' + i">
                      ✓ {{ etiquetaAceptada(a) }}</span>
                  </div>
                </div>
                <div class="ev done" v-if="reg.resolucion.confirmadaPor">
                  <div class="et">{{ $t('informeProp.confirmadaEl') }}</div>
                  <div class="em">{{ reg.resolucion.confirmadaPor }} · {{ fmtFechaHora(reg.resolucion.fechaConfirmacion) }}</div>
                </div>
                <div class="ev" v-if="reg.resolucion.descartadaPor">
                  <div class="et">{{ $t('informeProp.descartadaEl') }}</div>
                  <div class="em">{{ reg.resolucion.descartadaPor }} · {{ fmtFechaHora(reg.resolucion.fechaDescarte) }}</div>
                </div>
              </div>
              <p class="integridad">{{ $t('informeProp.integridad') }}</p>
            </div>
          </div>
        </template>

        <!-- HISTÓRICO -->
        <div class="section">
          <div class="sec-head"><span class="bar bar-lila"></span><h2>{{ $t('informeProp.historico') }}</h2>
            <span class="hint">{{ $t('informeProp.historicoHint') }}</span></div>
          <div class="sec-body">
            <div class="filtbar">
              <span class="fchip" :class="{ on: filtroEstado === null }" @click="setFiltro(null)">{{ $t('informeProp.todas') }}</span>
              <span class="fchip" v-for="e in ['PROPUESTA', 'ACEPTADA_PARCIAL', 'CONFIRMADA', 'DESCARTADA']" :key="'fe-' + e"
                    :class="{ on: filtroEstado === e }" @click="setFiltro(e)">{{ etiquetaEstado(e) }}</span>
            </div>
            <div class="sec-tabla">
              <table>
                <thead><tr><th>{{ $t('informeProp.fecha') }}</th><th>{{ $t('informeProp.usuario') }}</th>
                  <th>{{ $t('informeProp.inspector') }}</th><th>{{ $t('informeProp.ventana') }}</th>
                  <th class="der">{{ $t('informeProp.visitas') }}</th><th class="der">{{ $t('informeProp.pctAcept') }}</th>
                  <th>{{ $t('informeProp.estado') }}</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="h in historico" :key="'h-' + h.id">
                    <td>{{ fmtFechaHora(h.solicitud && h.solicitud.fecha) }}</td>
                    <td>{{ h.solicitud && h.solicitud.usuarioSolicitante }}</td>
                    <td>{{ h.solicitud && h.solicitud.inspector }}</td>
                    <td>{{ fmtFecha(h.solicitud && h.solicitud.desde) }}–{{ fmtFecha(h.solicitud && h.solicitud.hasta) }}</td>
                    <!-- #460 · «Visitas» y «% aceptadas» de la misma fila, en la misma unidad: el
                         porcentaje se calcula sobre este número, así que no puede salir de otro. -->
                    <td class="imp">{{ h.resultado ? visitasDeCorrida(h) : '—' }}</td>
                    <td class="imp">{{ pctAceptadas(h) }}</td>
                    <td><span class="stbadge" :class="claseEstado(h.resolucion && h.resolucion.estado)">{{ etiquetaEstado(h.resolucion && h.resolucion.estado) }}</span></td>
                    <td><a class="link" href="#" @click.prevent="cargar(h.id)">{{ $t('informeProp.ver') }}</a></td>
                  </tr>
                </tbody>
              </table>
              <div class="foot" v-if="!historico.length">{{ $t('informeProp.sinRegistros') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </f7-page>
</template>

<style scoped>
/* Estilo del mockup informe-propuesta-planificacion.html (paleta del dashboard de Control) */
.ipp{--plane:#f5f5fa;--surface:#fff;--surface-2:#fbfbfe;--ink:#12121a;--ink-2:#54545f;--muted:#8a8a97;
  --line:#e9e9f2;--line-2:#f0f0f7;--violet:#4a3aa7;--violet-2:#6d5ce0;--violet-soft:#efedfb;
  --ok:#0ca30c;--warn:#e08b00;--crit:#d03b3b;
  --m-plazo:#d03b3b;--m-urgencia:#eb6834;--m-cercania:#2a78d6;--m-secuencia:#1baf7a;
  --m-cierre:#4a3aa7;--m-zona:#eda100;--m-horario:#e87ba4;--m-relleno:#0e9aa7;
  --shadow:0 1px 2px rgba(18,18,26,.04),0 8px 24px rgba(18,18,26,.06);
  background:var(--plane);color:var(--ink);min-height:100%;line-height:1.45;
  font-family:system-ui,-apple-system,"Segoe UI",sans-serif}
.wrap{max-width:1200px;margin:0 auto;padding:20px 24px 80px}
.hero{position:relative;overflow:hidden;border-radius:22px;color:#fff;
  background:linear-gradient(135deg,#3d2f96 0%,#5a49c8 46%,#7b6bec 100%);padding:26px 30px;margin-bottom:22px}
.hero::after{content:"";position:absolute;right:-80px;top:-90px;width:340px;height:340px;border-radius:50%;
  background:radial-gradient(circle,rgba(255,255,255,.16),transparent 70%)}
.hero-top{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;position:relative;z-index:1;flex-wrap:wrap}
.hero h1{margin:0 0 4px;font-size:24px;letter-spacing:-.02em;font-weight:700}
.hero .sub{opacity:.92;font-size:14px;display:flex;gap:16px;flex-wrap:wrap;align-items:center;margin-top:8px}
.hero .who{opacity:.8;font-size:12.5px;margin-top:10px}
.hero-right{display:flex;flex-direction:column;gap:10px;align-items:flex-end}
.runbox{font-size:11.5px;opacity:.86;text-align:right;line-height:1.6}
.runbox b{font-family:ui-monospace,monospace}
.enac{display:inline-flex;align-items:center;gap:7px;background:rgba(255,255,255,.14);
  border:1px solid rgba(255,255,255,.24);padding:7px 12px;border-radius:999px;font-size:12px}
.enac .dot{width:7px;height:7px;border-radius:50%;background:#7effb2;box-shadow:0 0 0 4px rgba(126,255,178,.22)}
.estado{display:inline-flex;align-items:center;gap:8px;padding:8px 15px;border-radius:999px;font-weight:700;font-size:13px}
.estado .b{width:9px;height:9px;border-radius:50%;background:#fff}
.e-prop{background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.3)}
.e-acep{background:rgba(126,255,178,.2);border:1px solid rgba(126,255,178,.45)}
.e-acep .b{background:#7effb2}
.kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:12px;margin-bottom:22px}
@media(max-width:960px){.kpis{grid-template-columns:repeat(3,1fr)}}
.kpi{background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:15px 16px;box-shadow:var(--shadow)}
.kpi .lab{font-size:11.5px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.04em}
.kpi .val{font-size:27px;font-weight:750;letter-spacing:-.02em;margin-top:4px}
.kpi .val.small{font-size:22px}
.kpi .ctx{font-size:12px;color:var(--ink-2);margin-top:2px}
/* #460 · con qué se está contando, cuando no es el plan de la corrida. */
.kpi .ctx-nota{font-style:italic;color:var(--muted)}
.kpi.accent{background:linear-gradient(160deg,#fff,var(--violet-soft))}
.de-total{font-size:15px;color:var(--muted);font-weight:600}
.section{background:var(--surface);border:1px solid var(--line);border-radius:16px;box-shadow:var(--shadow);margin-bottom:18px;overflow:hidden}
.sec-head{display:flex;align-items:center;gap:12px;padding:16px 20px;border-bottom:1px solid var(--line-2)}
.sec-head .bar{width:4px;height:22px;border-radius:3px;background:var(--violet)}
.bar-azul{background:var(--m-cercania)!important}.bar-verde{background:var(--m-secuencia)!important}
.bar-roja{background:var(--crit)!important}.bar-ok{background:var(--ok)!important}.bar-lila{background:var(--violet-2)!important}
.sec-head h2{margin:0;font-size:16px;font-weight:700;letter-spacing:-.01em}
.sec-head .hint{margin-left:auto;font-size:12px;color:var(--muted)}
.sec-body{padding:18px 20px}
.sec-tabla{padding:0;overflow-x:auto}
.nota{margin:0 0 14px;font-size:13px;color:var(--ink-2)}
.frozen{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;color:var(--violet);
  background:var(--violet-soft);border:1px solid #ddd7f6;padding:4px 10px;border-radius:999px}
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(215px,1fr));gap:12px}
.field{background:var(--surface-2);border:1px solid var(--line-2);border-radius:10px;padding:11px 13px}
.field.span2{grid-column:span 2}
.field .k{font-size:11px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.03em}
.field .v{font-size:14.5px;font-weight:600;margin-top:3px}
.field .v small{font-weight:500;color:var(--ink-2)}
.chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px}
.chip{font-size:12px;background:#eef0f6;border:1px solid #e2e5ee;border-radius:999px;padding:2px 9px;font-weight:500}
.chip.veto{background:#fdecec;border-color:#f7d4d4;color:#b3312f}
.leyenda{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}
.day{border:1px solid var(--line);border-radius:16px;margin-bottom:12px;overflow:hidden;background:var(--surface)}
.day-head{display:flex;align-items:center;gap:14px;padding:13px 16px;cursor:pointer;user-select:none;background:linear-gradient(180deg,#fbfbfe,#f6f6fc)}
.day-head:hover{background:#f2f1fb}
.day-head .dchip{background:var(--violet);color:#fff;font-weight:700;font-size:12.5px;padding:5px 11px;border-radius:8px;white-space:nowrap}
.day-head .dtitle{font-weight:700;font-size:14.5px}
.day-head .dmeta{margin-left:auto;display:flex;gap:14px;align-items:center;font-size:12.5px;color:var(--ink-2)}
.day-head .dmeta b{color:var(--ink)}
/* El aviso de duración editada de la cabecera: se tiene que ver sin abrir el día, porque lo que
   avisa es que los minutos que hay a su izquierda no son los del calendario. */
.day-head .dmeta .dnota{font-weight:600;color:#8a5a00;background:#fff6e0;border:1px solid #f0dcae;border-radius:999px;padding:2px 9px}
.day-head .caret{transition:transform .2s;color:var(--muted);font-size:13px}
.day.collapsed .caret{transform:rotate(-90deg)}
.day.collapsed .day-body{display:none}
.barmini{width:120px;height:7px;border-radius:4px;background:#eceaf7;overflow:hidden;display:inline-block;vertical-align:middle;margin-left:6px}
.barmini > i{display:block;height:100%;background:linear-gradient(90deg,var(--violet-2),var(--violet));border-radius:4px}
.day-body{padding:6px 0 4px}
.visit{display:grid;grid-template-columns:74px 1fr;gap:14px;padding:12px 18px;border-top:1px solid var(--line-2)}
.visit:hover{background:#faf9ff}
/* #460 · bloque del solver que no llega a ser alta: se sigue viendo (es parte del proceso
   deductivo) pero apagado, y su fila dice el motivo. */
.visit.visit-omitida{opacity:.62}
.visit .time{font-weight:750;font-size:14px;font-variant-numeric:tabular-nums}
.visit .time small{display:block;font-weight:500;color:var(--muted);font-size:11px}
.visit .place{font-weight:650;font-size:14.5px}
.visit .svc{font-size:12.5px;color:var(--ink-2);margin-top:1px}
.visit .reason{margin-top:7px;display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.badge{font-size:11.5px;font-weight:700;color:#fff;padding:3px 10px;border-radius:999px;white-space:nowrap}
.b-plazo{background:var(--m-plazo)}.b-urgencia{background:var(--m-urgencia)}
.b-cercania{background:var(--m-cercania)}.b-secuencia{background:var(--m-secuencia)}
.b-cierre{background:var(--m-cierre)}.b-zona{background:var(--m-zona);color:#3a2c00}
.b-horario{background:var(--m-horario);color:#5a1233}
.b-relleno{background:var(--m-relleno)}
.detalle{font-size:13px;color:var(--ink-2)}
.detalle-mut{color:var(--muted);font-style:italic}
.alt{font-size:12.5px;color:var(--muted);margin-top:5px}
.alt::before{content:"↳ ";color:var(--m-cercania);font-weight:700}
.crit-chip{font-size:10.5px;font-weight:600;color:var(--ink-2);background:#f0f0f7;border:1px solid var(--line);padding:2px 8px;border-radius:6px}
table{width:100%;border-collapse:collapse;font-size:13px}
th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--muted);font-weight:600;padding:9px 12px;border-bottom:1px solid var(--line)}
th.der{text-align:right}
td{padding:10px 12px;border-bottom:1px solid var(--line-2);vertical-align:middle}
tbody tr:hover{background:#faf9ff}
.np-badge{font-size:11px;font-weight:700;padding:2px 9px;border-radius:999px;background:#fdecec;color:#b3312f;border:1px solid #f7d4d4;white-space:nowrap}
/* Pulido 2: tabla "Por qué NO" sin scroll horizontal — accionables ancladas a la derecha */
.tabla-no{table-layout:fixed}
.tabla-no .c-ref{width:150px}.tabla-no .c-pob{width:120px}.tabla-no .c-imp{width:80px}.tabla-no .c-link{width:112px}
.tabla-no .trunca{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.tabla-no .np-wrap{white-space:normal;display:inline-block;line-height:1.5}
.tabla-no .c-accion{text-align:right;padding-right:14px}
/* Pulido 1: chip TPI (producción de informes) — informativo, no anomalía */
.tpi-chip{font-size:11.5px;font-weight:700;color:#5b21b6;background:#efedfb;border:1px solid #ddd7f6;padding:3px 10px;border-radius:999px;white-space:nowrap}
.imp{font-variant-numeric:tabular-nums;font-weight:650;text-align:right}
.link{color:var(--violet);font-weight:650;font-size:12.5px;text-decoration:none;white-space:nowrap}
.link:hover{text-decoration:underline}
.tl{position:relative;padding-left:26px}
.tl .ev{position:relative;padding:0 0 16px 0}
.tl .ev::before{content:"";position:absolute;left:-20px;top:3px;width:11px;height:11px;border-radius:50%;background:var(--violet);border:2px solid #fff;box-shadow:0 0 0 2px var(--violet-soft)}
.tl .ev.done::before{background:var(--ok);box-shadow:0 0 0 2px #d7f2d7}
.tl::before{content:"";position:absolute;left:-14px;top:4px;bottom:8px;width:2px;background:var(--line)}
.tl .et{font-weight:700;font-size:13.5px}
.tl .em{font-size:12.5px;color:var(--ink-2);margin-top:1px}
.accepted{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.acc-pill{font-size:12px;font-weight:600;background:#e9f7ea;border:1px solid #c7ecc9;color:#1c7a2b;padding:4px 11px;border-radius:999px}
/* #460 · unidad del plan que sigue PENDIENTE (aceptación parcial): se ve, en gris, junto a las
   acreditadas. Lo que falta no puede ser solo la diferencia de un cociente. */
.acc-pill.acc-pend{background:var(--surface-2);border:1px dashed var(--line);color:var(--muted)}
.acc-pill.acc-desc{background:var(--surface-2);border:1px solid var(--line);color:var(--muted);text-decoration:line-through}
/* El motivo del descarte NO va tachado: lo tachado es el trabajo que no se hizo, y el motivo es lo
   que explica por qué — leerlo tachado lo hace parecer también anulado. */
.acc-pill.acc-desc .pill-motivo{text-decoration:none;font-style:normal;font-weight:500;color:var(--ink-2)}
.integridad{font-size:12.5px;color:var(--ink-2);margin:14px 0 0;padding:11px 13px;background:var(--surface-2);border:1px solid var(--line-2);border-radius:10px}
.filtbar{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;align-items:center}
.fchip{font-size:12.5px;font-weight:600;padding:6px 13px;border-radius:999px;border:1px solid var(--line);background:var(--surface);cursor:pointer;color:var(--ink-2);transition:.12s}
.fchip:hover{border-color:var(--violet-2);color:var(--violet)}
.fchip.on{background:var(--violet);border-color:var(--violet);color:#fff}
.stbadge{font-size:11px;font-weight:700;padding:2px 9px;border-radius:999px;white-space:nowrap}
.st-prop{background:#eef0f6;color:#4a5568;border:1px solid #e2e5ee}
.st-acep{background:#e9f7ea;color:#1c7a2b;border:1px solid #c7ecc9}
.st-conf{background:var(--violet-soft);color:var(--violet);border:1px solid #ddd7f6}
.st-desc{background:#fdecec;color:#b3312f;border:1px solid #f7d4d4}
.foot{text-align:center;color:var(--muted);font-size:12px;padding:16px}

/* #16 · sello de "de cuando son estos datos", mismo sitio en todas las pantallas. */
.sello-datos-bar { padding: 6px 16px 0; display: flex; flex-wrap: wrap; gap: 8px; }
</style>

<script>
import Planificacion from '../../js/db/Planificacion.js';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';
import MotivosPlanificacion from '../../js/MotivosPlanificacion.js';
import RecuentoPropuesta from '../../js/RecuentoPropuesta.js';
import UnidadAceptable from '../../js/UnidadAceptable.js';
import SelloDatos from '../../components/SelloDatos.vue';

export default {
  components: { SelloDatos },
  data() {
    return {
      MOTIVOS: MotivosPlanificacion.MOTIVOS,
      reg: null,
      lineasCtx: {},
      pedidosCtx: {},
      historico: [],
      filtroEstado: null,
      abiertos: {},
      cargando: false
    };
  },
  computed: {
    par() { return (this.reg && this.reg.solicitud.parametros) || {}; },
    snap() { return this.reg && this.reg.snapshotRestricciones; },
    totales() { return (this.reg && this.reg.resultado.totales) || {}; },
    aceptadas() { return (this.reg && this.reg.resolucion.aceptadas) || []; },
    fechaResolucion() {
      const r = this.reg && this.reg.resolucion;
      if (!r) return null;
      return r.fechaConfirmacion || r.fechaDescarte || (r.aceptadas && r.aceptadas.length ? r.aceptadas[0].fecha : null);
    },
    diasVentana() {
      if (!this.reg) return 0;
      const ms = new Date(this.reg.solicitud.hasta) - new Date(this.reg.solicitud.desde);
      return Math.max(1, Math.round(ms / 86400000));
    },
    horasCampo() {
      return (Math.round((this.totales.minutosPropuestos || 0) / 60 * 10) / 10).toLocaleString('es-ES') + ' h';
    },
    // #460 (opción A) · TODO lo que esta pantalla dice «cuántas» sale de `RecuentoPropuesta`, que NO
    // deduce nada: LEE el plan de unidades que derivó la API y las altas que acreditó. El
    // KPI de visitas, el de aceptadas, la cronología, las cabeceras de día y el % del histórico
    // salen del mismo sitio, porque dos partes de la misma pantalla no pueden decir lo contrario la
    // una de la otra (§9 del CLAUDE.md del orquestador). Y una corrida sin plan no se disimula: se
    // presenta con su base de recuento.
    recuentoCorrida() { return RecuentoPropuesta.recuento(this.reg); },
    trabajosPropuestos() {
      const c = this.recuentoCorrida;
      return c.conDenominador ? c.propuestos : '?';
    },
    trabajosAceptados() { return this.recuentoCorrida.aceptados; },
    /** Qué se está contando, para decirlo en pantalla cuando no es el plan de la corrida. */
    notaRecuento() {
      const base = this.recuentoCorrida.base;
      if (base === RecuentoPropuesta.BASE_PLAN) {
        // Una corrida resuelta con descartes no está «aceptada del todo» —un bloque descartado no
        // se convierte en alta— pero tampoco le falta nada por decidir, y el cociente solo no lo
        // dice: el número va dentro de la frase para que se pueda ir a buscarlos abajo.
        //
        // Que haya descartes NO es que no queden pendientes: aceptar una, descartar otra y dejar
        // más sin decidir es lo normal, y ahí el KPI decía «no quedan pendientes» contradiciendo a
        // las píldoras pendientes de la sección Resolución, dos partes de la misma pantalla
        // diciendo lo contrario (P2 de Codex, ronda 1 del 16/10/2026). Así que la frase de corrida
        // resuelta la dice el recuento, no la presencia de un descarte.
        const c = this.recuentoCorrida;
        if (!c.descartados) return null;
        return c.pendientes > 0
          ? this.$t('informeProp.recuentoConDescartesPendientes', { n: c.descartados, p: c.pendientes })
          : this.$t('informeProp.recuentoConDescartes', { n: c.descartados });
      }
      return this.$t(base === RecuentoPropuesta.BASE_SIN_PLAN
        ? 'informeProp.recuentoSinPlan' : 'informeProp.recuentoLegado');
    },
    /** Lo que queda por decidir: unidades del plan sin alta creada y sin descartar. */
    unidadesPendientes() { return RecuentoPropuesta.pendientes(this.reg); },
    /**
     * Las unidades que alguien DESCARTÓ a mano antes de aceptarlas, CON EL DESCARTE AL LADO. Se
     * pintan aparte de las pendientes porque no son lo mismo: una pendiente puede aceptarse todavía
     * y una descartada ya se decidió. Dejarlas mezcladas es lo que hacía que una corrida resuelta
     * entera pareciera tener trabajo sin atender.
     *
     * El descarte viaja con la unidad porque el motivo, quién lo descartó y cuándo están guardados
     * en él, y antes se quedaban sin pintar: la píldora gris salía muda y había que ir al registro
     * para saber qué había pasado con ese trabajo (prueba de dirección del 16/10/2026).
     */
    unidadesDescartadas() {
      const fuera = RecuentoPropuesta.descartadas(this.reg);
      const out = [];
      RecuentoPropuesta.unidades(this.reg).forEach(function(u) {
        const d = u && u.unidadId ? fuera[u.unidadId] : null;
        if (d) out.push({ unidad: u, descarte: d });
      });
      return out;
    },
    /** Dónde el alta se creó con otra duración que la propuesta, por unidad del plan. */
    duracionesEditadas() { return RecuentoPropuesta.duracionesEditadas(this.reg); },
    /**
     * Las unidades del plan que SÍ tienen alta creada, en el orden del plan. Es el numerador, y es
     * también lo que se pinta: una píldora por unidad acreditada, con lo que el plan guardó de ella
     * —tipo incluido—, así que un informe de oficina ya no necesita deducirse por la ausencia de
     * pedido. En las corridas sin plan no hay unidades: ahí se siguen pintando las aceptadas
     * trazadas, que es lo que esas corridas tienen.
     */
    unidadesAcreditadas() {
      const hechas = RecuentoPropuesta.acreditadas(this.reg);
      return RecuentoPropuesta.unidades(this.reg).filter(function(u) {
        return !!(u && u.unidadId && hechas[u.unidadId]);
      });
    },
    hayPlan() { return !!RecuentoPropuesta.plan(this.reg); },
    criterioCorrida() {
      const ex = (this.reg && this.reg.resultado.explicaciones) || [];
      const criterios = {};
      ex.forEach(function(e) { if (e.criterio) criterios[e.criterio] = true; });
      return Object.keys(criterios).join(' + ') || '—';
    },
    // Explicaciones por lineaId EXACTO (registros nuevos guardan lineaId en el resumen). Fallback
    // para registros antiguos: parse "pedido#linea" del prefijo (best-effort, puede no casar).
    explicacionesPorLineaId() {
      const out = {};
      ((this.reg && this.reg.resultado.explicaciones) || []).forEach(function(e) {
        if (e.lineaId && out[e.lineaId] === undefined) out[e.lineaId] = e;
      });
      return out;
    },
    explicacionesPorLinea() {
      const out = {};
      ((this.reg && this.reg.resultado.explicaciones) || []).forEach(function(e) {
        const m = /^(\d+)-(\d+)/.exec(e.lineaId || '');
        if (m && out[m[1] + '#' + parseInt(m[2], 10)] === undefined) out[m[1] + '#' + parseInt(m[2], 10)] = e;
      });
      return out;
    },
    // Visitas agrupadas por día (del resumen del registro + contexto enriquecido + explicación unida)
    dias() {
      const self = this;
      const resumen = (self.reg && self.reg.resultado.propuestasResumen) || [];
      // #460 (opción A) · qué hace el Aceptar con cada bloque sale del PLAN guardado, no de deducirlo
      // fila a fila: a qué unidad va, si es un tramo de una visita partida y si se queda fuera (con
      // su motivo). Así la cabecera del día dice cuántas ALTAS salen de ese día y las filas de abajo
      // —que son bloques del solver, y pueden ser más— dicen cada una lo que es. Un bloque que no
      // crea alta deja de parecer una unidad sin aceptar.
      const porBloque = RecuentoPropuesta.porBloque(self.reg);
      // #460 · Dónde el alta se creó con otra duración que la propuesta. Los minutos que se pintan
      // aquí —los de la fila y los del «colocado» de la cabecera— son SIEMPRE los PROPUESTOS, que
      // es lo que mide esta pantalla, así que cuando no coinciden con los del alta hay que decirlo:
      // si no, el informe enseña 1 h 30 mientras el calendario tiene 2 h 14 y las dos pantallas se
      // contradicen sin que nada lo explique (prueba de dirección del 16/10/2026).
      const editadas = RecuentoPropuesta.duracionesEditadas(self.reg);
      const porDia = {};
      resumen.forEach(function(p, idx) {
        const f = p.horaInicio ? new Date(p.horaInicio) : null;
        const clave = f ? f.toISOString().slice(0, 10) : 'sin-fecha';
        const ctx = p.numeroPedido != null && p.numLinea != null
          ? self.contextoLinea(p.numeroPedido, p.numLinea) : {};
        const esTarea = p.tipo === 'TAREA';
        // Una fila, una suerte: `tomar` CONSUME la del plan para este bloque. Dos bloques idénticos
        // comparten clave y el Aceptar hace con ellos cosas distintas —uno crea el alta y el otro se
        // omite—, así que leerlos de un índice dejaba las dos filas con la misma.
        const hecho = porBloque.tomar(p);
        // #460 · si el plan dice de qué tipo es la unidad, se lee; solo cuando no hay plan (o el
        // bloque no está en él) se cae al criterio deducido. Deducirlo teniéndolo escrito es la
        // misma forma de fallo que descuadraba los recuentos.
        const esTpi = (hecho && hecho.unidad)
          ? hecho.unidad.tipo === UnidadAceptable.TIPO_INFORME : self.esBloqueTpi(p);
        // La duración editada es de la UNIDAD, no del bloque: una visita que el solver partió en
        // tramos se crea como un alta con los minutos sumados, así que la nota va en el bloque que
        // origina el alta y no se repite en cada tramo.
        const editada = (hecho && !hecho.omitido && !hecho.consolidado && hecho.unidadId)
          ? (editadas[hecho.unidadId] || null) : null;
        porDia[clave] = porDia[clave] || { clave: clave, fecha: f, minutos: 0, visitas: [],
          unidades: {}, sueltos: 0, editadas: 0 };
        porDia[clave].minutos += p.minutos || 0;
        if (editada) porDia[clave].editadas += 1;
        if (!hecho) {
          // Corrida sin plan (o bloque que el plan no recoge): se cuenta el bloque, que es la
          // interpretación histórica, en vez de perderlo o de inventarle una unidad.
          porDia[clave].sueltos += 1;
        } else if (!hecho.omitido && hecho.unidadId) {
          porDia[clave].unidades[hecho.unidadId] = true;
        }
        porDia[clave].visitas.push({
          clave: idx,
          esTpi: esTpi,
          omitido: !!(hecho && hecho.omitido),
          editada: editada,
          hora: f ? ('0' + f.getHours()).slice(-2) + ':' + ('0' + f.getMinutes()).slice(-2) : '—',
          minutos: p.minutos || 0,
          lugar: esTpi
            ? self.$t('informeProp.tpip')
            : (((ctx.poblacion ? ctx.poblacion + ' — ' : '') + (ctx.cliente || p.codigoPedido || p.numeroPedido || '')) || '—'),
          servicio: [ctx.materia, (p.codigoPedido || p.numeroPedido) && p.numLinea != null
              ? (p.codigoPedido || p.numeroPedido) + ' / L' + p.numLinea : null,
            ctx.importe != null ? self.eur(ctx.importe) : null,
            esTarea && p.numeroPedido != null ? self.$t('informeProp.continuacion') : null,
            self.notaDelBloque(hecho),
            self.notaDuracionEditada(editada)]
            .filter(Boolean).join(' · '),
          explicacion: (p.lineaId && self.explicacionesPorLineaId[p.lineaId])
            || (p.numeroPedido != null && p.numLinea != null
                ? self.explicacionesPorLinea[p.numeroPedido + '#' + p.numLinea] : null)
        });
      });
      const arr = Object.keys(porDia).sort().map(function(k) { return porDia[k]; });
      const DIAS = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
      arr.forEach(function(d) {
        d.etiqueta = d.fecha ? DIAS[d.fecha.getDay()] + ' ' + d.fecha.getDate() : '—';
        // Las altas que salen de este día: las unidades del plan con bloque aquí, más los bloques
        // que el plan no recoge. Sumadas por días dan el KPI de arriba, que es la misma unidad.
        d.nTrabajos = Object.keys(d.unidades).length + d.sueltos;
        d.visitas.sort(function(a, b) { return a.hora < b.hora ? -1 : 1; });
      });
      return arr;
    },
    // #460 · El importe se suma UNA VEZ POR LÍNEA, no por bloque: una línea se factura una sola vez
    // por mucho que el planificador la parta en tramos o la reparta en varios días, y sumando
    // bloque a bloque el KPI contaba dos veces el dinero de la misma línea. Es el mismo olvido que
    // descuadraba los recuentos —contar bloques donde la unidad es otra—, así que la clave sale del
    // mismo módulo.
    importePropuesto() {
      const self = this;
      const contadas = {};
      let s = 0;
      ((self.reg && self.reg.resultado.propuestasResumen) || []).forEach(function(p) {
        const clave = RecuentoPropuesta.claveLinea(p);
        if (clave == null || contadas[clave]) return;
        contadas[clave] = true;
        const ctx = self.contextoLinea(p.numeroPedido, p.numLinea);
        if (ctx.importe != null) s += ctx.importe;
      });
      return s;
    },
    noColocadas() {
      const self = this;
      return ((self.reg && self.reg.resultado.noPlanificables) || []).map(function(np) {
        const m = /^(\d+)-(\d+)/.exec(np.lineaId || '');
        const ped = m ? m[1] : null;
        // Importe SOLO si la línea exacta casó (el fallback por pedido daría el € de OTRA línea)
        const ctxLinea = m ? self.lineasCtx[ped + '#' + parseInt(m[2], 10)] : null;
        const ctx = ctxLinea || (ped && self.pedidosCtx[ped]) || {};
        return {
          referencia: m ? (ctx.codigoPedido || m[1]) + ' / L' + parseInt(m[2], 10) : (np.expediente || np.lineaId),
          cliente: ctx.cliente, poblacion: ctx.poblacion, idPedido: ctx.idPedido,
          importe: ctxLinea ? ctxLinea.importe : null, motivo: np.motivo, detalle: np.detalle
        };
      });
    },
    importeNoColocadas() {
      return this.noColocadas.reduce(function(s, n) { return s + (n.importe || 0); }, 0);
    },
    motivosNoColocadas() {
      const m = {};
      this.noColocadas.forEach(function(n) { m[n.detalle || n.motivo || '?'] = true; });
      return Object.keys(m).length;
    }
  },
  mounted() {
    const id = this.$f7route && this.$f7route.params ? this.$f7route.params.registroId : null;
    this.cargarHistorico();
    if (id) this.cargar(id); else this.cargarUltimo();
  },
  methods: {
    eur(v) { return Math.round(v || 0).toLocaleString('es-ES') + ' €'; },
    n1(v) { return (Math.round((v || 0) * 10) / 10).toLocaleString('es-ES'); },
    kmDelDia(clave) {
      const m = this.reg && this.reg.resultado.kmPorDia;
      return m && m[clave] != null ? m[clave] : null;
    },
    horas(min) { return Math.floor((min || 0) / 60) + ' h ' + ('0' + ((min || 0) % 60)).slice(-2); },
    fmtFecha(d) { return d ? new Date(d).toLocaleDateString('es-ES') : '—'; },
    /** Día corto (DD/MM) para las píldoras de aceptadas; sin fecha devuelve null, no "hoy". */
    fmtDiaCorto(d) {
      if (!d) return null;
      const f = new Date(d);
      if (isNaN(f.getTime())) return null;
      return ('0' + f.getDate()).slice(-2) + '/' + ('0' + (f.getMonth() + 1)).slice(-2);
    },
    // Singular/plural de los rótulos con cifra de esta pantalla. En la prueba del 15/10/2026 salió
    // «por 1 motivos»; es la misma forma de fallo en siete sitios, así que la decide una sola
    // función por rótulo y ninguno interpola la cifra en un literal en plural fijo.
    textoPorMotivos(n) {
      return this.$t(n === 1 ? 'informeProp.porMotivo' : 'informeProp.porMotivos', { n: n });
    },
    textoEnDias(n) {
      return this.$t(n === 1 ? 'informeProp.enDia' : 'informeProp.enDias', { n: n });
    },
    textoPorqueNo(n) {
      return this.$t(n === 1 ? 'informeProp.porqueNo1' : 'informeProp.porqueNo', { n: n });
    },
    textoVisitasPropuestas(n) {
      return this.$t(n === 1 ? 'informeProp.visitaPropuesta' : 'informeProp.visitasPropuestas', { n: n });
    },
    textoAceptadasDe(n, t) {
      return this.$t(n === 1 ? 'informeProp.aceptadaDe' : 'informeProp.aceptadasDe', { n: n, t: t });
    },
    palabraDias(n) { return this.$t(n === 1 ? 'informeProp.dia' : 'informeProp.dias'); },
    palabraMeses(n) { return this.$t(n === 1 ? 'informeProp.mes' : 'informeProp.meses'); },
    palabraVisitas(n) { return this.$t(n === 1 ? 'informeProp.visita' : 'informeProp.visitas'); },
    /**
     * ¿Este bloque es producción de informes (TPI)? CRITERIO ÚNICO del informe: lo usan el proceso
     * deductivo (S3) y las píldoras de aceptadas (S4), que antes lo decidían por separado — y la
     * segunda no lo decidía en absoluto, de ahí las tres píldoras mudas de la prueba del 15/10/2026.
     * lineaId "TPIP-<fecha>" (agregado por día del mapper) o sufijo "-INF" (informe individual);
     * respaldo, tarea sin pedido. NO se infiere por ausencia de motivo: una visita de CAMPO sin
     * motivo tiene que seguir cantando como anomalía.
     */
    esBloqueTpi(p) {
      if (!p) return false;
      if (p.lineaId && (/^TPIP-/.test(p.lineaId) || /-INF$/.test(p.lineaId))) return true;
      return p.tipo === 'TAREA' && p.numeroPedido == null;
    },
    // La unidad de recuento NO se decide aquí: `RecuentoPropuesta` LEE el plan de unidades que
    // declaró el Aceptar y las altas que acreditó, los mismos campos del registro que usa el panel
    // «¿Por qué?» del calendario y con los que `RegistroPropuestasService` decide en el backend si
    // la corrida es ACEPTADA o ACEPTADA_PARCIAL. Estos métodos son solo la puerta a ese módulo.
    cocienteAceptadas(reg) { return RecuentoPropuesta.recuento(reg); },
    /** Visitas de una corrida del histórico, en la MISMA unidad que su % de aceptadas. */
    visitasDeCorrida(h) {
      const c = RecuentoPropuesta.recuento(h);
      return c.conDenominador ? c.propuestos : '?';
    },
    /**
     * Qué hizo el plan con este bloque, dicho en su fila: de qué visita es un tramo, o por qué no
     * crea alta. Sale del plan guardado —no se deduce aquí— y es lo que evita que una fila sin alta
     * se lea como una unidad que nadie aceptó. `null` cuando no hay nada que añadir.
     */
    notaDelBloque(hecho) {
      if (!hecho) return null;
      if (hecho.omitido) {
        return hecho.motivo
          ? this.$t('informeProp.bloqueSinAltaPorque', { motivo: hecho.motivo })
          : this.$t('informeProp.bloqueSinAlta');
      }
      return hecho.total > 1
        ? this.$t('informeProp.tramoDe', { i: hecho.indice, n: hecho.total }) : null;
    },
    /**
     * «La duración se editó al aceptar», con los dos lados. Los minutos de la fila son los
     * propuestos —la medición se hace con los que vio el planificador, decisión de dirección del
     * 10/10/2026— así que la nota no corrige la cifra: dice que el alta se creó con otra, para que
     * quien compare el informe con el calendario no vea dos verdades sin explicación. `null` cuando
     * la duración es la propuesta, que es lo normal.
     */
    notaDuracionEditada(editada) {
      if (!editada) return null;
      return this.$t('informeProp.duracionEditada',
        { prop: this.horas(editada.propuestos), acep: this.horas(editada.aceptados) });
    },
    /** El aviso de la cabecera del día: cuántos trabajos de ese día se aceptaron con otra duración. */
    textoDuracionEditada(n) {
      return this.$t(n === 1 ? 'informeProp.diaConDuracionEditada'
        : 'informeProp.diaConDuracionesEditadas', { n: n });
    },
    /** Quién descartó la unidad y cuándo; el motivo ya se pinta en la propia píldora. */
    ayudaDescartada(d) {
      const quien = d && d.usuario ? d.usuario : '?';
      return this.$t('informeProp.descartadaAyuda') + ' — '
        + this.$t('informeProp.descartadaPorEl', { usuario: quien, fecha: this.fmtFechaHora(d && d.fecha) });
    },
    /**
     * Qué es una unidad del plan, en una píldora: el tipo lo dice el plan, así que un informe de
     * oficina no necesita deducirse por la ausencia de pedido —las tres píldoras mudas «✓ · / L» de
     * la prueba del 15/10/2026 eran justo eso—. Y la continuación multidía se etiqueta como tal,
     * que es el caso en el que dos unidades comparten pedido y línea legítimamente.
     */
    etiquetaUnidad(u) {
      if (!u) return this.$t('informeProp.sinIdentidad');
      const partes = [];
      if (u.tipo === UnidadAceptable.TIPO_INFORME) {
        partes.push(this.$t('informeProp.tpip'));
      } else {
        const ref = u.codigoPedido || u.numeroPedido;
        if (ref != null && u.numLinea != null) {
          partes.push(this.contextoLinea(u.numeroPedido, u.numLinea).cliente || String(ref));
          partes.push(ref + ' / L' + u.numLinea);
        } else if (ref != null) {
          partes.push(this.contextoLinea(u.numeroPedido, u.numLinea).cliente || String(ref));
        } else if (u.lineaId) {
          partes.push(this.$t('informeProp.bloqueDeLaCorrida', { id: u.lineaId }));
        } else {
          partes.push(this.$t('informeProp.sinIdentidad'));
        }
        if (u.tipo === UnidadAceptable.TIPO_CONTINUACION) {
          partes.push(this.$t('informeProp.continuacionCorta'));
        }
      }
      // El DÍA DEL PLAN manda sobre la hora: `u.dia` es el día de negocio que fijó la API y
      // `fmtDiaCorto` rotula en la zona del dispositivo, así que en un navegador en UTC una visita
      // de Madrid que empieza a las 00:30 salía con el día anterior y señalaba a la unidad
      // equivocada (P2 de Codex, ronda 1 del 16/10/2026). La hora queda como respaldo para las
      // unidades que no traigan día.
      const dia = this.diaLegible(u.dia) || this.fmtDiaCorto(u.horaInicio);
      if (dia) partes.push(dia);
      return partes.join(' · ');
    },
    /** Un día 'YYYY-MM-DD' del plan tal como se lee ('DD/MM'); null si no lo trae. */
    diaLegible(dia) {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia || '');
      return m ? m[3] + '/' + m[2] : null;
    },
    /**
     * Qué se aceptó, en una píldora. Tres formas, y ninguna muda: informe de oficina (TPI) con su
     * día; línea de campo con cliente, pedido/línea y día; y lo que no tenga ni pedido ni línea, con
     * su identidad del solver o diciendo que no la trae. La continuación multidía se etiqueta como
     * tal: es el caso en el que dos píldoras comparten pedido y línea legítimamente.
     */
    etiquetaAceptada(a) {
      const dia = this.fmtDiaCorto(a && a.horaInicio);
      const partes = [];
      if (this.esBloqueTpi(a)) {
        partes.push(this.$t('informeProp.tpip'));
      } else {
        const ref = (a && (a.codigoPedido || a.numeroPedido)) || null;
        if (ref != null && a.numLinea != null) {
          partes.push(this.contextoLinea(a.numeroPedido, a.numLinea).cliente || String(ref));
          partes.push(ref + ' / L' + a.numLinea);
        } else if (ref != null) {
          partes.push(this.contextoLinea(a.numeroPedido, a.numLinea).cliente || String(ref));
          partes.push(String(ref));
        } else if (a && a.lineaId) {
          partes.push(this.$t('informeProp.bloqueDeLaCorrida', { id: a.lineaId }));
        } else {
          partes.push(this.$t('informeProp.sinIdentidad'));
        }
        if (a && a.tipo === 'TAREA' && a.numeroPedido != null) {
          partes.push(this.$t('informeProp.continuacionCorta'));
        }
      }
      if (dia) partes.push(dia);
      return partes.join(' · ');
    },
    fmtFechaHora(d) {
      if (!d) return '—';
      const f = new Date(d);
      return f.toLocaleDateString('es-ES') + ' ' + f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    triestado(v) { return v === true ? this.$t('informeProp.si') : (v === false ? this.$t('informeProp.no') : '—'); },
    claseMotivo: MotivosPlanificacion.claseMotivo,
    badgeMotivo: MotivosPlanificacion.badgeMotivo,
    contextoLinea(numeroPedido, numLinea) {
      return this.lineasCtx[numeroPedido + '#' + numLinea] || this.pedidosCtx[String(numeroPedido)] || {};
    },
    etiquetaEstado(e) {
      return e ? (this.$t('informeProp.estado' + e) !== 'informeProp.estado' + e
        ? this.$t('informeProp.estado' + e) : e) : '—';
    },
    claseEstado(e) {
      return { PROPUESTA: 'st-prop', ACEPTADA: 'st-acep', ACEPTADA_PARCIAL: 'st-acep',
               CONFIRMADA: 'st-conf', DESCARTADA: 'st-desc' }[e] || 'st-prop';
    },
    // El % del histórico sale de la MISMA función que el KPI «Aceptadas» de arriba: dos sitios de la
    // misma pantalla no pueden dar cifras distintas de lo mismo, ni tampoco elegir cada uno su
    // unidad. La búsqueda devuelve el registro completo, así que aquí hay el mismo resumen que en el
    // informe.
    pctAceptadas(h) {
      const c = this.cocienteAceptadas(h);
      return (c.conDenominador && c.propuestos)
        ? Math.round(c.aceptados / c.propuestos * 100) + ' %' : '—';
    },
    cargar(id) {
      const self = this;
      const app = self.$f7;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `finally` que lo soltaba.
      cerrojoDeLaSesion(self, 'cargando');
      Planificacion.informePropuesta({ data: { id: id } }).then(function(res) {
        const d = (res && res.dto) ? res.dto : res;
        self.reg = d.registro;
        self.lineasCtx = d.lineas || {};
        self.pedidosCtx = d.pedidos || {};
        self.abiertos = {};
      }).catch(function(e) {
        if (typeof e === 'string') e = { error: e };
        app.dialog.alert(e.message || e.error || 'No se pudo cargar el informe.');
      }).finally(function() { self.cargando = false; });
    },
    cargarUltimo() {
      const self = this;
      Planificacion.registroPropuestas({ data: { limite: 1 } }).then(function(res) {
        const arr = ((res && res.dto) ? res.dto : res) || [];
        if (arr.length) self.cargar(arr[0].id);
      }).catch(function() {});
    },
    cargarHistorico() {
      const self = this;
      Planificacion.registroPropuestas({ data: { limite: 30, estado: self.filtroEstado } }).then(function(res) {
        self.historico = ((res && res.dto) ? res.dto : res) || [];
      }).catch(function() { self.historico = []; });
    },
    setFiltro(e) {
      this.filtroEstado = e;
      this.cargarHistorico();
    }
  }
};
</script>
